/**
 * Auth + tenant headers shared by the axios interceptor (`config/axios.ts`)
 * and the SSE fetch in `aiChatApi.ts`. Reads localStorage directly because
 * axios is imported before Pinia is initialized — the original axios setup
 * had to do the same dance, this just centralizes it so non-axios callers
 * get identical behavior.
 */

// Roles that may send `X-Hospital-Id: ALL` — kept in sync with the backend
// ActiveHospitalGuard.
const ROLES_WITH_ALL_ACCESS = new Set(['admin', 'super_admin']);

interface PersistedHospitalContext {
  activeHospitalId: string | null;
}

interface PersistedAuth {
  token?: string;
  refreshToken?: string;
  user?: { role?: string };
}

function readJson<T>(key: string): T | null {
  const raw = localStorage.getItem(key);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export function readAuthContext(): PersistedAuth | null {
  return readJson<PersistedAuth>('auth');
}

export function readHospitalContext(): PersistedHospitalContext | null {
  return readJson<PersistedHospitalContext>('hospitalContext');
}

/**
 * Returns the `Authorization` and `X-Hospital-Id` headers an authenticated
 * request needs. Empty object when there's no token (anonymous request).
 */
export function buildAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const auth = readAuthContext();
  if (!auth?.token) return headers;

  headers.Authorization = `Bearer ${auth.token}`;

  const role = auth.user?.role;
  if (!role || role === 'patient') return headers;

  const activeId = readHospitalContext()?.activeHospitalId ?? null;
  if (activeId) {
    headers['X-Hospital-Id'] = activeId;
  } else if (ROLES_WITH_ALL_ACCESS.has(role)) {
    headers['X-Hospital-Id'] = 'ALL';
  }
  // Else leave the header absent — backend will 400 with a clear message.
  return headers;
}
